import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatQuantity, type UnitKey } from '@fewerbags/core';
import { STORAGE_LOCATIONS } from '@fewerbags/schema';
import type { PantryItemDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { Field } from '../Field';
import { SelectField } from '../SelectField';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { isoToDateInputValue, LOCATION_LABEL, type StorageLocation } from '../../lib/pantryDisplay';
import { UNIT_OPTIONS } from '../../lib/units';
import { ExpiryBadge } from './ExpiryBadge';

const LOCATION_OPTIONS = STORAGE_LOCATIONS.map((l) => ({ value: l, label: LOCATION_LABEL[l] }));

/** View/edit/consume/delete sheet for one pantry item. */
export function PantryItemDialog({
  item,
  open,
  onClose,
}: {
  item: PantryItemDto | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState<UnitKey>('count');
  const [location, setLocation] = useState<StorageLocation>('pantry');
  const [expiresOn, setExpiresOn] = useState('');
  const [consumeQty, setConsumeQty] = useState('');
  const [consumeUnit, setConsumeUnit] = useState<UnitKey>('count');
  const [consumeResult, setConsumeResult] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    setQty(String(item.qty));
    setUnit(item.unit);
    setLocation(item.location);
    setExpiresOn(isoToDateInputValue(item.expiresOn));
    setConsumeQty('');
    setConsumeUnit(item.unit);
    setConsumeResult(null);
  }, [item]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['pantry'] });

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.pantry.update>[1]) =>
      api.pantry.update(item!.id, input),
    onSuccess: () => invalidate(),
  });
  const remove = useMutation({
    mutationFn: () => api.pantry.remove(item!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const consume = useMutation({
    mutationFn: (input: Parameters<typeof api.pantry.consume>[1]) =>
      api.pantry.consume(item!.id, input),
    onSuccess: (res) => {
      invalidate();
      if (res.removed) {
        setConsumeResult('Fully consumed — removed from the pantry.');
        setTimeout(onClose, 1200);
      } else if (res.item) {
        setConsumeResult(`${formatQuantity(res.item.qty, res.item.unit)} left.`);
      }
    },
  });

  function onSaveEdits(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!item) return;
    const qtyNum = Number(qty);
    update.mutate({
      qty: Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : undefined,
      unit,
      location,
      expiresOn: expiresOn ? expiresOn : null,
    });
  }

  function onToggleOpened() {
    if (!item) return;
    update.mutate({ openedAt: item.openedAt ? null : new Date().toISOString() });
  }

  function onConsume(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setConsumeResult(null);
    const qtyNum = Number(consumeQty);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) return;
    consume.mutate({ qty: qtyNum, unit: consumeUnit });
  }

  if (!item) {
    return (
      <Dialog open={open} onClose={onClose} title="Pantry item">
        <p className="text-sm text-muted">No item selected.</p>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={item.ingredientName}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted">
            {formatQuantity(item.qty, item.unit)} · {LOCATION_LABEL[item.location]}
          </p>
          <ExpiryBadge daysUntilExpiry={item.daysUntilExpiry} />
        </div>

        <form onSubmit={onSaveEdits} noValidate className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field
              id="pantry-edit-qty"
              label="Quantity"
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
            <SelectField
              id="pantry-edit-unit"
              label="Unit"
              options={UNIT_OPTIONS}
              value={unit}
              onChange={(e) => setUnit(e.target.value as UnitKey)}
            />
          </div>
          <SelectField
            id="pantry-edit-location"
            label="Location"
            options={LOCATION_OPTIONS}
            value={location}
            onChange={(e) => setLocation(e.target.value as StorageLocation)}
          />
          <Field
            id="pantry-edit-expires"
            label="Expiry date"
            type="date"
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
          />
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onToggleOpened}
              className="min-h-9 rounded-lg border border-line px-3 text-sm"
            >
              {item.openedAt ? 'Mark as unopened' : 'Mark as opened'}
            </button>
            {item.openedAt ? (
              <span className="text-xs text-muted">
                Opened {new Date(item.openedAt).toLocaleDateString()}
              </span>
            ) : null}
          </div>
          {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
          <Button type="submit" disabled={update.isPending}>
            Save changes
          </Button>
        </form>

        <form
          onSubmit={onConsume}
          noValidate
          className="flex flex-col gap-3 border-t border-line pt-4"
        >
          <h3 className="text-sm font-semibold text-ink">Use some up</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field
              id="pantry-consume-qty"
              label="Quantity used"
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              value={consumeQty}
              onChange={(e) => setConsumeQty(e.target.value)}
            />
            <SelectField
              id="pantry-consume-unit"
              label="Unit"
              options={UNIT_OPTIONS}
              value={consumeUnit}
              onChange={(e) => setConsumeUnit(e.target.value as UnitKey)}
            />
          </div>
          {consume.isError ? <Alert tone="error">{messageForError(consume.error)}</Alert> : null}
          {consumeResult ? <Alert tone="success">{consumeResult}</Alert> : null}
          <Button type="submit" variant="secondary" disabled={consume.isPending}>
            {consume.isPending ? 'Recording…' : 'Consume'}
          </Button>
        </form>

        <div className="border-t border-line pt-4">
          {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`Remove ${item.ingredientName} from the pantry?`)) remove.mutate();
            }}
          >
            Delete item
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
